import React, { useCallback, useContext, useEffect, useState } from 'react';
import './Map.scss';
import { Map, ScaleControl } from 'react-map-gl';
import {
  IMapProps,
  getBounds,
  MAPBOX_ACCESS_TOKEN,
  MAPBOX_STYLE_TOKEN
} from './MapHelper';
import { isEmpty } from 'underscore';
import { StoreContext } from '../../store/store';
import { getGrid } from './Layer/graticule';
import 'mapbox-gl/dist/mapbox-gl.css';
import { PointPopup } from './Control/Popup';
import { FWELAYER, INSITU_LAYER } from './Layer/MarkerLayer';
import {MapboxOverlay, MapboxOverlayProps} from '@deck.gl/mapbox/typed';
import {useControl} from 'react-map-gl';
import { ViewportProps } from '../../typings/map';

function DeckGLOverlay(props: MapboxOverlayProps & {
  interleaved?: boolean;
}) {
  const overlay = useControl<MapboxOverlay>(() => new MapboxOverlay(props));
  overlay.setProps(props);
  return null;
}

const MapComponent = (props: IMapProps)  => {
  const {
    markerCollections,
    setGraticule,
    setMapBoundsZoom,
    setActivePopupPoint, 
  } = useContext(StoreContext);

  const {
    popupContent, popupCoordinates, onPopupCoordinatesChanged, onClosePopup,
    layers, isMark, center, onMouseMove,
    onClick, updateBounds, dragPan, children,
  } = props;

  const [viewState, setViewState] = useState<ViewportProps>({
    height: 400,
    width: 400,
    latitude: 31,
    longitude: -55,
    zoom: 1,
    transitionDuration: 1000,
    pitch: 0,
    bearing: 0,
  });

  const [isHoveringActivePoint, setIsHoveringActivePoint] = useState(false)
 
  const point = markerCollections.find((x) => x.active);

  const updateMapBounds = (vp: ViewportProps, update = false) => {
    const bounds = getBounds(vp);
    const nw = bounds[0];
    const se = bounds[1];
    const longBegin = nw[0];
    const longEnd = se[0];
    const latBegin = nw[1];
    const latEnd = se[1];
    const lngBegin = longBegin < -180 ? -180 : longBegin;
    const lngEnd = longEnd > 180 ? 180 : longEnd;
    setGraticule(
      getGrid([lngBegin, latBegin, lngEnd, latEnd], 5000, 5000, 'kilometers')
    );
    if (update || updateBounds) {
      setMapBoundsZoom({
        bounds: [longBegin, latBegin, longEnd, latEnd],
        zoom: vp.zoom,
      });
    }
  };

  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    if (loaded && center && !isEmpty(center)){
      const { longitude, latitude, zoom } = center
      setViewState((previous) => {
        return {...previous, longitude, latitude, zoom}
      })
    }
  }, [center, loaded])

  const handleOnLoad = useCallback((evt: any) => {
    updateMapBounds(viewState, true);
    setLoaded(true)
  }, [viewState])

  const handleZoomPan = (evt: any) => {
    const newViewState = evt.viewState;
    const vp = {...newViewState, width: evt.target._containerWidth, height: evt.target._containerHeight};
    updateMapBounds(vp);
  }

  const handleOnResize = useCallback((evt: any) => {
    const vp = {...viewState, width: evt.target._containerWidth, height: evt.target._containerHeight};
    updateMapBounds(vp);
  }, [viewState])

  const handleViewportChange = (evt: any) => {
    setViewState(evt.viewState);
  }
 
  return (
    <div
      className={
        isMark ? 'map-container adding-marker' : 'map-container'
      }
    >
      <Map
        key="map"
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        mapStyle={MAPBOX_STYLE_TOKEN}
        dragPan={dragPan}
        cursor={isMark ? 'pointer' : 'default'}
        {...viewState}
        onMove={handleViewportChange}
        onMouseMove={onMouseMove}
        onZoomEnd={handleZoomPan}
        onDragEnd={handleZoomPan}
        onLoad={handleOnLoad}
        onResize={handleOnResize}
        style={
          {
            width: '100vw',
            height: '100vh',
          }
        }    
      >
        <DeckGLOverlay
          layers={[...layers]} 
          getTooltip={({ object }) =>
          (object as any)?.value
            ? {
              html: `
                        <div>${(object as any)?.value} [m]</div>
                        <div>${point?.surfaceMaps.selectedLevel2}</div>`,
            }
            : isHoveringActivePoint
              ? (object as any).active === true
                ? {
                  html: `
                          <div>Move Point</div>
                          <div>Click and hold to move point</div>`,
                }
                : null
              : null
          }
          onHover={
            (info, ev: any) => {
              if (ev) {
                const layerId = info?.layer?.id               
                setIsHoveringActivePoint(layerId === 'icon-layer' && (info as any)?.object?.active)              
              }
            }
          }
          onClick={
            (info, ev: any) => {
              if (ev) {
                if (isMark) {
                  onClick!(info.coordinate as number[]);
                }
                else {
                  const object = info?.object
                  if (info?.layer?.id === FWELAYER) {
                    setActivePopupPoint(FWELAYER)
                  } else if (info?.layer?.id === INSITU_LAYER) {
                    setActivePopupPoint(INSITU_LAYER)
                  }
                  if (onPopupCoordinatesChanged && (info?.layer?.id === INSITU_LAYER || info?.layer?.id === FWELAYER)) {
                    object && onPopupCoordinatesChanged(object);
                  }
                }
              }
            }
          }
        />
        {popupContent && popupCoordinates !== null &&
          <PointPopup popup={{ coordinates: popupCoordinates, onClose: onClosePopup }}>
            {popupContent}
          </PointPopup>
        }
        {children}
        <ScaleControl
          unit="metric"
          maxWidth={150}
        />
        <ScaleControl
          unit="imperial"
          maxWidth={150}
        />
      </Map>
    </div>
  );
}

export default MapComponent;
